import React, { useState, useRef, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import { uploadImageAndGetUrl, deleteImage, getSignedImageUrl, getBucketForCategory } from '../../../utils/imageUpload';
import { calendarService } from '../../../services/artistContentService';
import { useAuth } from '../../../contexts/AuthContext';

const CalendarSection = ({ artistData, onUpdate, isActive }) => {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [uploadingStates, setUploadingStates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState('');
  const fileRef = useRef(null);

  // Focus management for event title inputs
  const [activeInput, setActiveInput] = useState(null);
  const titleRefs = useRef({});

  // Focus management for event description textareas
  const [activeDescription, setActiveDescription] = useState(null);
  const descriptionRefs = useRef({});

  // Focus management for event URL inputs
  const [activeUrl, setActiveUrl] = useState(null);
  const urlRefs = useRef({});

  // Event type options
  const eventTypeOptions = [
    { value: 'Performance', label: 'Performance' },
    { value: 'Signing', label: 'Signing' },
    { value: 'Release', label: 'Release' },
    { value: 'Merch Drop', label: 'Merch Drop' }
  ];

  const [newEntry, setNewEntry] = useState({
    title: "",
    date: "",
    time: "",
    image: null,
    imagePath: null,
    url: "",
    body: "",
    hasUrl: false,
    eventType: "Performance", // Default to Performance
    price: "",
    venueName: "",
    venueAddress: ""
  });

  // Load calendar events on component mount
  useEffect(() => {
    if (user?.id) {
      loadEvents();
    }
  }, [user?.id]);

  const loadEvents = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const { data, error } = await calendarService?.getEvents(user?.id);
      
      if (error) {
        console.error('Error loading events:', error);
        setSaveError(`Failed to load events: ${error?.message}`);
        return;
      }

      if (data) {
        // Get signed URLs for images
        const eventsWithImages = await Promise.all(
          data?.map(async (event) => {
            let imageUrl = null;
            if (event?.image_path) {
              // FIX: Try promotion bucket first, then fall back to profile bucket
              let bucket = getBucketForCategory('promotion');
              let urlResult = await getSignedImageUrl(bucket, event?.image_path, 7200);
              
              if (urlResult?.error) {
                console.log(`Event image not found in promotion bucket, trying profile bucket for ${event?.image_path}`);
                bucket = getBucketForCategory('profile');
                urlResult = await getSignedImageUrl(bucket, event?.image_path, 7200);
              }
              
              if (urlResult?.data) {
                imageUrl = urlResult?.data?.signedUrl;
              }
            }

            return {
              id: event?.id,
              title: event?.title,
              date: event?.event_date,
              time: event?.event_time,
              image: imageUrl,
              imagePath: event?.image_path,
              url: event?.event_url || '',
              body: event?.body || '',
              hasUrl: event?.has_url || false,
              eventType: event?.event_type || 'Performance',
              price: event?.price || '',
              venueName: event?.venue_name || '',
              venueAddress: event?.venue_address || ''
            };
          })
        );

        setEntries(eventsWithImages);
      }
    } catch (error) {
      console.error('Error loading events:', error);
      setSaveError('Failed to load calendar events');
    } finally {
      setLoading(false);
    }
  };

  // Focus management handlers
  const handleTitleFocus = (entryId) => {
    setActiveInput(entryId);
  };

  const handleTitleBlur = (entryId) => {
    setTimeout(() => {
      if (activeInput === entryId) {
        setActiveInput(null);
      }
    }, 100);
  };

  const handleTitleChange = (entryId, value, isNew = false) => {
    if (isNew) {
      setNewEntry(prev => ({ ...prev, title: value }));
    } else {
      setEditingEntry(prev => ({ ...prev, title: value }));
    }

    if (activeInput === entryId) {
      setTimeout(() => {
        const ref = titleRefs?.current?.[entryId];
        if (ref && document.activeElement !== ref) {
          ref?.focus();
        }
      }, 0);
    }
  };

  // Focus management handlers for description
  const handleDescriptionFocus = (entryId) => {
    setActiveDescription(entryId);
  };

  const handleDescriptionBlur = (entryId) => {
    setTimeout(() => {
      if (activeDescription === entryId) {
        setActiveDescription(null);
      }
    }, 100);
  };

  const handleDescriptionChange = (entryId, value, isNew = false) => {
    if (isNew) {
      setNewEntry(prev => ({ ...prev, body: value }));
    } else {
      setEditingEntry(prev => ({ ...prev, body: value }));
    }

    if (activeDescription === entryId) {
      setTimeout(() => {
        const ref = descriptionRefs?.current?.[entryId];
        if (ref && document.activeElement !== ref) {
          ref?.focus();
          const length = ref?.value?.length || 0;
          ref?.setSelectionRange(length, length);
        }
      }, 0);
    }
  };

  // Focus management handlers for URL
  const handleUrlFocus = (entryId) => {
    setActiveUrl(entryId);
  };

  const handleUrlBlur = (entryId) => {
    setTimeout(() => {
      if (activeUrl === entryId) {
        setActiveUrl(null);
      }
    }, 100);
  };

  const handleUrlChange = (entryId, value, isNew = false) => {
    if (isNew) {
      setNewEntry(prev => ({ ...prev, url: value }));
    } else {
      setEditingEntry(prev => ({ ...prev, url: value }));
    }

    if (activeUrl === entryId) {
      setTimeout(() => {
        const ref = urlRefs?.current?.[entryId];
        if (ref && document.activeElement !== ref) {
          ref?.focus();
          const length = ref?.value?.length || 0;
          ref?.setSelectionRange(length, length);
        }
      }, 0);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, dropIndex) => {
    e?.preventDefault();
    
    if (draggedItem === null) return;
    
    const newEntries = [...entries];
    const draggedEntry = newEntries?.[draggedItem];
    
    newEntries?.splice(draggedItem, 1);
    newEntries?.splice(dropIndex, 0, draggedEntry);
    
    setEntries(newEntries);
    setDraggedItem(null);

    const reorderData = newEntries?.map((entry, index) => ({
      id: entry?.id,
      sort_order: index
    }));

    const { error } = await calendarService?.reorderEvents(reorderData);
    if (error) {
      setSaveError(`Failed to reorder events: ${error?.message}`);
    }
  };

  const handleImageUpload = async (file, isNew = false, entryId = null) => {
    if (!file || !file?.type?.startsWith('image/') || !user?.id) {
      return;
    }

    const uploadKey = isNew ? 'newEntry' : entryId || 'editing';
    setUploadingStates(prev => ({ ...prev, [uploadKey]: true }));

    try {
      let oldImagePath = null;
      if (isNew && newEntry?.imagePath) {
        oldImagePath = newEntry?.imagePath;
      } else if (editingEntry?.imagePath) {
        oldImagePath = editingEntry?.imagePath;
      }

      if (oldImagePath) {
        // FIX: Use promotion bucket for calendar event images
        let bucket = getBucketForCategory('promotion');
        await deleteImage(bucket, oldImagePath);
      }

      // FIX: Upload calendar event images to promotion bucket
      const result = await uploadImageAndGetUrl(file, user?.id, 'promotion');
      
      if (result?.error) {
        setSaveError(`Upload failed: ${result?.error}`);
        return;
      }

      if (isNew) {
        setNewEntry(prev => ({ 
          ...prev, 
          image: result?.data?.url,
          imagePath: result?.data?.path
        }));
      } else if (editingEntry) {
        setEditingEntry(prev => ({ 
          ...prev, 
          image: result?.data?.url,
          imagePath: result?.data?.path
        }));
      }

    } catch (error) {
      setSaveError('Failed to upload image');
      console.error('Image upload error:', error);
    } finally {
      setUploadingStates(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const startEdit = (entry) => {
    setEditingEntry({ ...entry });
    setShowAddForm(false);
    setSaveError('');
  };

  const saveEdit = async () => {
    if (!editingEntry?.id) return;

    try {
      const { data, error } = await calendarService?.updateEvent(editingEntry?.id, {
        title: editingEntry?.title,
        date: editingEntry?.date,
        time: editingEntry?.time,
        imagePath: editingEntry?.imagePath,
        url: editingEntry?.url,
        body: editingEntry?.body,
        hasUrl: editingEntry?.hasUrl,
        eventType: editingEntry?.eventType,
        price: editingEntry?.price ? parseFloat(editingEntry?.price) : null,
        venueName: editingEntry?.venueName,
        venueAddress: editingEntry?.venueAddress
      });

      if (error) {
        setSaveError(`Failed to update event: ${error?.message}`);
        return;
      }

      await loadEvents();
      setEditingEntry(null);
      
    } catch (error) {
      setSaveError('Failed to update event');
      console.error('Update error:', error);
    }
  };

  const cancelEdit = () => {
    setEditingEntry(null);
    setSaveError('');
  };

  const deleteEntry = async (id) => {
    if (!id) return;

    try {
      const entryToDelete = entries?.find(entry => entry?.id === id);
      if (entryToDelete?.imagePath) {
        // FIX: Try both buckets when deleting
        const promotionBucket = getBucketForCategory('promotion');
        const profileBucket = getBucketForCategory('profile');
        
        // Try to delete from both buckets (one will fail silently)
        await Promise.allSettled([
          deleteImage(promotionBucket, entryToDelete?.imagePath),
          deleteImage(profileBucket, entryToDelete?.imagePath)
        ]);
      }

      const { error } = await calendarService?.deleteEvent(id);
      if (error) {
        setSaveError(`Failed to delete event: ${error?.message}`);
        return;
      }

      await loadEvents();
      
    } catch (error) {
      setSaveError('Failed to delete event');
      console.error('Delete error:', error);
    }
  };

  const addNewEntry = async () => {
    if (!newEntry?.title || !newEntry?.date || !user?.id) {
      setSaveError('Please provide event title and date');
      return;
    }

    try {
      const { data, error } = await calendarService?.createEvent(user?.id, {
        title: newEntry?.title,
        date: newEntry?.date,
        time: newEntry?.time,
        imagePath: newEntry?.imagePath,
        url: newEntry?.url,
        body: newEntry?.body,
        hasUrl: newEntry?.hasUrl,
        eventType: newEntry?.eventType,
        price: newEntry?.price ? parseFloat(newEntry?.price) : null,
        venueName: newEntry?.venueName,
        venueAddress: newEntry?.venueAddress,
        sortOrder: entries?.length
      });

      if (error) {
        setSaveError(`Failed to create event: ${error?.message}`);
        return;
      }

      setNewEntry({
        title: "",
        date: "",
        time: "",
        image: null,
        imagePath: null,
        url: "",
        body: "",
        hasUrl: false,
        eventType: "Performance",
        price: "",
        venueName: "",
        venueAddress: ""
      });
      
      setShowAddForm(false);
      await loadEvents();
      
    } catch (error) {
      setSaveError('Failed to create event');
      console.error('Create error:', error);
    }
  };

  const handleSave = async () => {
    onUpdate?.('calendar', entries);
  };

  const EntryForm = ({ entry, onChange, onImageUpload, isNew = false }) => {
    const uploadKey = isNew ? 'newEntry' : entry?.id || 'editing';
    const isUploading = uploadingStates?.[uploadKey];
    const entryId = isNew ? 'new-entry' : entry?.id;
    
    return (
      <div className="space-y-4 p-4 border border-border rounded-lg bg-card">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-sm font-medium text-text-primary">Event Title</label>
            <input
              ref={(el) => {
                if (el) titleRefs.current[entryId] = el;
              }}
              type="text"
              placeholder="Enter event title"
              value={entry?.title || ''}
              onChange={(e) => handleTitleChange(entryId, e?.target?.value, isNew)}
              onFocus={() => handleTitleFocus(entryId)}
              onBlur={() => handleTitleBlur(entryId)}
              className="w-full px-3 py-2 bg-input border border-border rounded-lg text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-accent"
              required
            />
          </div>
          
          <div className="space-y-1">
            <Select
              label="Event Type"
              value={entry?.eventType || 'Performance'}
              onChange={(value) => onChange({ ...entry, eventType: value })}
              options={eventTypeOptions}
              placeholder="Select event type"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="Date"
            type="date"
            value={entry?.date || ''}
            onChange={(e) => onChange({ ...entry, date: e?.target?.value })}
            required
          />
          
          <Input
            label="Time"
            type="time"
            value={entry?.time || ''}
            onChange={(e) => onChange({ ...entry, time: e?.target?.value })}
          />
          
          <Input
            label="Price ($)"
            type="number"
            step="0.01"
            min="0"
            placeholder="0.00"
            value={entry?.price || ''}
            onChange={(e) => onChange({ ...entry, price: e?.target?.value })}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Venue Name"
            type="text"
            placeholder="Enter venue name"
            value={entry?.venueName || ''}
            onChange={(e) => onChange({ ...entry, venueName: e?.target?.value })}
          />
          
          <Input
            label="Venue Address"
            type="text"
            placeholder="Enter venue address"
            value={entry?.venueAddress || ''}
            onChange={(e) => onChange({ ...entry, venueAddress: e?.target?.value })}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-text-primary">Event Image</label>
          <div className="space-y-4">
            {entry?.image && (
              <div className="relative inline-block">
                <Image
                  src={entry?.image}
                  alt="Event preview"
                  className="w-20 h-20 object-cover rounded-lg"
                />
                {!isUploading && (
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute -top-2 -right-2 w-6 h-6"
                    onClick={() => {
                      onChange({ ...entry, image: null, imagePath: null });
                    }}
                  >
                    <Icon name="X" size={12} />
                  </Button>
                )}
              </div>
            )}
            
            <div className="flex items-center space-x-4">
              {isUploading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm text-text-secondary">Uploading...</span>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileRef?.current?.click()}
                  iconName="Upload"
                  iconPosition="left"
                  iconSize={16}
                >
                  {entry?.image ? 'Change Image' : 'Upload Image'}
                </Button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={(e) => onImageUpload(e?.target?.files?.[0], isNew, entry?.id)}
                className="hidden"
                disabled={isUploading}
              />
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id={`hasUrl-${entry?.id || 'new'}`}
              checked={entry?.hasUrl || false}
              onChange={(e) => onChange({ ...entry, hasUrl: e?.target?.checked })}
              className="rounded border-border bg-input text-accent focus:ring-accent focus:ring-2"
            />
            <label htmlFor={`hasUrl-${entry?.id || 'new'}`} className="text-sm font-medium text-text-primary">
              Include external link
            </label>
          </div>
          
          {entry?.hasUrl && (
            <div className="space-y-1">
              <label className="text-sm font-medium text-text-primary">Event URL</label>
              <input
                ref={(el) => {
                  if (el) urlRefs.current[entryId] = el;
                }}
                type="url"
                placeholder="https://example.com/event"
                value={entry?.url || ''}
                onChange={(e) => handleUrlChange(entryId, e?.target?.value, isNew)}
                onFocus={() => handleUrlFocus(entryId)}
                onBlur={() => handleUrlBlur(entryId)}
                className="w-full px-3 py-2 bg-input border border-border rounded-lg text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
          )}
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-text-primary">Event Description</label>
          <textarea
            ref={(el) => {
              if (el) descriptionRefs.current[entryId] = el;
            }}
            value={entry?.body || ''}
            onChange={(e) => handleDescriptionChange(entryId, e?.target?.value, isNew)}
            onFocus={() => handleDescriptionFocus(entryId)}
            onBlur={() => handleDescriptionBlur(entryId)}
            rows={3}
            className="w-full px-3 py-2 bg-input border border-border rounded-lg resize-none text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-accent"
            placeholder="Describe your event..."
          />
        </div>
      </div>
    );
  };

  if (!isActive) return null;

  if (loading) {
    return (
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm text-text-secondary">Loading calendar events...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-warning rounded-lg flex items-center justify-center">
            <Icon name="Calendar" size={20} color="white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Calendar Events</h2>
            <p className="text-sm text-text-secondary">Manage your upcoming events and performances</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowAddForm(true)}
            iconName="Plus"
            iconPosition="left"
            iconSize={16}
          >
            Add Event
          </Button>
        </div>
      </div>

      {saveError && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-sm text-destructive">{saveError}</p>
        </div>
      )}

      {/* Add New Entry Form */}
      {showAddForm && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-text-primary">Add New Event</h3>
            <Button variant="ghost" size="sm" onClick={() => setShowAddForm(false)}>
              <Icon name="X" size={16} />
            </Button>
          </div>
          
          <EntryForm
            entry={newEntry}
            onChange={setNewEntry}
            onImageUpload={handleImageUpload}
            isNew={true}
          />
          
          <div className="flex justify-end space-x-2 mt-4">
            <Button variant="outline" size="sm" onClick={() => setShowAddForm(false)}>
              Cancel
            </Button>
            <Button 
              variant="default" 
              size="sm" 
              onClick={addNewEntry}
              disabled={uploadingStates?.newEntry}
            >
              Add Event
            </Button>
          </div>
        </div>
      )}

      {/* Calendar Entries List */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2 text-sm text-text-secondary">
          <Icon name="Move" size={16} />
          <span>Drag entries to reorder them</span>
        </div>

        {entries?.map((entry, index) => (
          <div key={entry?.id}>
            {editingEntry && editingEntry?.id === entry?.id ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-text-primary">Edit Event</h3>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" onClick={cancelEdit}>
                      Cancel
                    </Button>
                    <Button 
                      variant="default" 
                      size="sm" 
                      onClick={saveEdit}
                      disabled={Object.values(uploadingStates)?.some(Boolean)}
                    >
                      Save
                    </Button>
                  </div>
                </div>
                
                <EntryForm
                  entry={editingEntry}
                  onChange={setEditingEntry}
                  onImageUpload={handleImageUpload}
                />
              </div>
            ) : (
              <div
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                className="flex items-center space-x-4 p-4 bg-card border border-border rounded-lg hover:bg-muted/30 cursor-move transition-colors"
              >
                <Icon name="GripVertical" size={20} className="text-text-secondary" />
                
                <Image
                  src={entry?.image || '/assets/images/no_image.png'}
                  alt={entry?.title}
                  className="w-16 h-16 object-cover rounded-lg"
                />
                
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="font-medium text-text-primary">{entry?.title}</h4>
                    <span className="px-2 py-1 text-xs rounded-full bg-accent/10 text-accent">
                      {entry?.eventType}
                    </span>
                  </div>
                  <p className="text-sm text-text-secondary">
                    {new Date(entry?.date)?.toLocaleDateString()} {entry?.time && `at ${entry?.time}`}
                  </p>
                  {entry?.venueName && (
                    <p className="text-xs text-text-secondary">
                      📍 {entry?.venueName}
                    </p>
                  )}
                  {entry?.price && (
                    <p className="text-xs text-success font-medium">
                      ${parseFloat(entry?.price)?.toFixed(2)}
                    </p>
                  )}
                  {entry?.hasUrl && entry?.url && (
                    <p className="text-xs text-accent">Has external link</p>
                  )}
                </div>
                
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => startEdit(entry)}
                  >
                    <Icon name="Edit" size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteEntry(entry?.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Icon name="Trash2" size={16} />
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Status Indicator */}
      <div className="mt-6 p-4 bg-card rounded-lg border border-border">
        <div className="flex items-center space-x-2">
          <Icon 
            name={entries?.length > 0 ? "CheckCircle" : "AlertCircle"} 
            size={16} 
            className={entries?.length > 0 ? "text-success" : "text-warning"}
          />
          <span className="text-sm font-medium text-text-primary">
            Calendar Status: {entries?.length > 0 ? `${entries?.length} events scheduled` : "No events"}
          </span>
        </div>
        {entries?.length === 0 && (
          <p className="text-sm text-text-secondary mt-1">
            Add at least one event to populate your calendar section.
          </p>
        )}
      </div>
    </div>
  );
};

export default CalendarSection;